/**
 * 고용관리 페이지 (V45.1 → V45.2 → V46)
 *
 * 퇴사 예약, 계약 연장, 고용형태 변경 등 employment 관련 일괄 관리
 *
 * **탭 (V46 개편):**
 * - 계약만료 임박: endDate가 30일 이내 (기본 탭)
 * - 직원검색: 검색 버튼 클릭 시에만 조회 (필터 변경으로 자동 조회 안 함)
 * - 예약 정보: 퇴사예정/고용형태변경 예약된 모든 항목
 * - 히스토리: 실행 완료된 고용관리 로그 (날짜 범위 필터)
 *
 * **V46 변경사항:**
 * - 기본 탭을 '계약만료 임박'으로 변경 (계약만료 놓치지 않기 위함)
 * - 탭 라벨 '계약만료 임박(30일)' → '계약만료 임박'으로 단순화
 * - 직원검색 탭: 검색 버튼 클릭이 유일한 조회 트리거 (필터 변경 시 자동 조회 방지)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Calendar,
  RefreshCw,
  Clock,
  FileText,
  Download,
  PlayCircle, // V55.28
} from 'lucide-react';
import { Pagination, paginateData } from '../../shared/components/Pagination';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { exportEmploymentHistoryToCSV } from '../../shared/utils/csvExport';
import {
  useEmploymentManagementList,
  useScheduleTermination,
  useChangeTerminationDate,
  useCancelTermination,
  useExtendContract,
  useChangeEmploymentType,
  useEmploymentManagementHistory,
  useManualTriggerEmploymentTypeChange, // V55.28
  EmploymentManagementTab,
  EmploymentManagementItem,
  EmploymentTypeCode,
} from '../../shared/hooks/useEmploymentManagement';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { formatDateTimeByCompanyCode } from '../../shared/utils/dateUtils';  // V62
import { TerminationScheduleModal } from './components/employmentManagement/TerminationScheduleModal';
import { ContractExtensionModal } from './components/employmentManagement/ContractExtensionModal';
import { EmploymentTypeChangeModal } from './components/employmentManagement/EmploymentTypeChangeModal';
import { EmploymentDetailManagementModal, type TabType as DetailTabType } from './components/employmentManagement/EmploymentDetailManagementModal';

// V253: 액션 피커 타입 정의
type EmploymentActionType = 'terminate' | 'extend' | 'typeChange';

const ACTION_TO_DETAIL_TAB: Record<EmploymentActionType, DetailTabType> = {
  terminate: 'termination',
  extend: 'extension',
  typeChange: 'typeChange',
};

const ACTION_PICKER_OPTIONS: { action: EmploymentActionType; label: string; description: string; color: string }[] = [
  { action: 'terminate', label: '퇴사 예약', description: '퇴사 예정일을 설정합니다', color: '#ef4444' },
  { action: 'extend', label: '계약 연장', description: '계약 종료일을 연장합니다', color: '#059669' },
  { action: 'typeChange', label: '고용형태 변경', description: '고용형태를 변경합니다', color: '#2563eb' },
];

// V255.2: 예약 탭용 — 예약 단위 행 (직원 1명에 예약 복수 시 행 분해)
interface ReservationRow {
  item: EmploymentManagementItem;
  reservationType: DetailTabType;
}

// 고용형태 옵션 (검색/만료임박/예약정보 탭용)
const EMPLOYMENT_TYPE_OPTIONS = [
  { code: '', name: '전체' },
  { code: 'DISPATCH', name: '파견직' },
  { code: 'INTERN', name: '인턴' },
  { code: 'CONTRACT', name: '계약직' },
  { code: 'REGULAR', name: '정규직' },
];


// 탭 정의 (V46: 계약만료 임박을 첫 번째 탭으로 변경)
const TABS: { id: EmploymentManagementTab; label: string; icon: React.ReactNode }[] = [
  { id: 'expiring', label: '계약만료 임박', icon: <Clock size={16} /> },
  { id: 'search', label: '직원검색', icon: <Search size={16} /> },
  { id: 'reservations', label: '예약 정보', icon: <Calendar size={16} /> },
  { id: 'history', label: '히스토리', icon: <FileText size={16} /> },
];

export function HrEmploymentManagementPage() {
  // V279: 접근 권한은 ProtectedRoute(canAccessRoute)에서 ROLE_PERMISSIONS 기반으로 이미 체크됨.
  // 기존 useAuthorization().hasPermission은 Firestore auth_roles.permissions(BE용 형식)을
  // 사용하여 FE용 권한 키(hr.employees.read)와 불일치 → role_exec 접근 차단 버그 발생.
  // 중복 체크 제거하여 ProtectedRoute와 일관성 유지.
  return <EmploymentManagementContent />;
}

// V45.4: 탭별 필터 상태 타입
interface TabFilters {
  searchQuery: string;
  employmentTypeCode: string;
}

const defaultFilters: TabFilters = {
  searchQuery: '',
  employmentTypeCode: '',
};

// V15 Issue 2: 액션 타입 한글 매핑
const ACTION_TYPE_NAMES: Record<string, string> = {
  'TERMINATION_SCHEDULED': '퇴사 예약',
  'TERMINATION_DATE_CHANGED': '퇴사 예정일 변경',
  'TERMINATION_CANCELED': '퇴사 예약 취소',
  'TERMINATION_EXECUTED': '퇴사 처리',
  'CONTRACT_EXTENDED': '계약 연장',
  'CONTRACT_EXTENSION_SCHEDULED': '계약 연장 예약',
  'CONTRACT_EXTENSION_CANCELED': '계약 연장 예약 취소',
  'EMPLOYMENT_TYPE_CHANGED': '고용형태 변경',
  'EMPLOYMENT_TYPE_CHANGE_SCHEDULED': '고용형태 변경 예약',
  'EMPLOYMENT_TYPE_CHANGE_EXECUTED': '고용형태 변경 실행',   // V312: BE enum 누락 보완
  'EMPLOYMENT_TYPE_CHANGE_CANCELED': '고용형태 변경 예약 취소',
};

function EmploymentManagementContent() {
  // V55.28: 관리자 권한 체크
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission('hr.manage');

  // 상태 (V46: 기본 탭을 '계약만료 임박'으로 변경)
  const [activeTab, setActiveTab] = useState<EmploymentManagementTab>('expiring');
  const [selectedItem, setSelectedItem] = useState<EmploymentManagementItem | null>(null);

  // V45.5: 탭 전환 시 깜빡임 방지 - 마지막으로 데이터를 로드한 탭 추적
  const [lastLoadedTab, setLastLoadedTab] = useState<EmploymentManagementTab>('expiring');

  // V45.4: 탭별 필터 상태 분리 (각 탭이 마지막 값 기억)
  const [filtersByTab, setFiltersByTab] = useState<Record<EmploymentManagementTab, TabFilters>>({
    search: { ...defaultFilters },
    expiring: { ...defaultFilters },
    reservations: { ...defaultFilters },
    history: { ...defaultFilters },
  });

  // V46: 직원검색 탭 전용 - 검색 버튼 클릭 시 카운터 증가로 조회 트리거
  const [searchTriggerCount, setSearchTriggerCount] = useState(0);

  // 현재 탭의 필터
  const currentFilters = filtersByTab[activeTab];

  // 현재 탭의 필터 업데이트 함수
  const updateCurrentFilter = (key: keyof TabFilters, value: string) => {
    setFiltersByTab((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: value,
      },
    }));
  };

  // V45.2: 히스토리 탭 날짜 범위
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');


  // 모달 상태
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationModalMode, setTerminationModalMode] = useState<'schedule' | 'change'>('schedule');
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [showTypeChangeModal, setShowTypeChangeModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false); // V45.2: 통합 상세 관리 모달
  // V253: 액션 피커 + URL query 연동
  const [showActionPicker, setShowActionPicker] = useState(false);
  const [selectedAction, setSelectedAction] = useState<DetailTabType | null>(null);
  // V255: 진입 경로 추적 (액션 피커 경유 vs 예약 직접 진입)
  const [enteredViaActionPicker, setEnteredViaActionPicker] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // V62: URL ?tab= 파라미터로 초기 탭 지정 (대시보드 연동)
  const tabInitRef = useRef(false);
  useEffect(() => {
    if (tabInitRef.current) return;
    const t = searchParams.get('tab');
    if (t && TABS.some(tab => tab.id === t)) {
      tabInitRef.current = true;
      setActiveTab(t as EmploymentManagementTab);
    }
  }, [searchParams]);

  // Hooks - V46: 직원검색 탭은 searchTriggerCount가 증가할 때만 조회
  const { items, totalCount, isLoading, error, refetch } = useEmploymentManagementList(
    activeTab === 'history' ? 'search' : activeTab, // V45.2: history 탭은 별도 처리
    {
      employmentTypeCode: currentFilters.employmentTypeCode as EmploymentTypeCode | undefined,
      searchQuery: currentFilters.searchQuery || undefined,
    },
    { searchTriggerCount }
  );

  // V45.2: 히스토리 hook
  const historyHook = useEmploymentManagementHistory();

  // V71: 히스토리 페이지네이션
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 15;

  // V71: 커서 기반 데이터 자동 로드 (V71.1: error 시 무한 루프 방지)
  useEffect(() => {
    if (historyHook.hasNext && !historyHook.isLoading && !historyHook.error) {
      historyHook.fetchNext();
    }
  }, [historyHook.hasNext, historyHook.isLoading, historyHook.error]);

  // V45.5: 탭 전환 시 깜빡임 방지
  // 데이터 로딩 완료 시 lastLoadedTab 업데이트
  const prevIsLoading = useRef(isLoading);
  useEffect(() => {
    // isLoading이 true → false로 변경될 때 (로딩 완료)
    if (prevIsLoading.current && !isLoading && activeTab !== 'history') {
      setLastLoadedTab(activeTab);
    }
    prevIsLoading.current = isLoading;
  }, [isLoading, activeTab]);

  // V45.5: 탭이 변경되었는지 확인 (데이터 불일치 감지)
  const isTabDataMismatch = activeTab !== 'history' && activeTab !== lastLoadedTab;

  // V47: 예약정보 탭용 필터링 - 예약이 있는 항목만
  const reservationItems = items.filter(
    (i) => i.terminationPlanned || i.contractExtensionPlanned || i.employmentTypeChangePlanned
  );

  // V255.2: 예약 단위 행 분해 (1직원 = 최대 3행, 각 행에 reservationType 명시)
  const reservationRows: ReservationRow[] = [];
  for (const item of reservationItems) {
    if (item.contractExtensionPlanned) reservationRows.push({ item, reservationType: 'extension' });
    if (item.employmentTypeChangePlanned) reservationRows.push({ item, reservationType: 'typeChange' });
    if (item.terminationPlanned) reservationRows.push({ item, reservationType: 'termination' });
  }

  // V47: 현재 탭에서 사용할 실제 아이템 목록
  const displayItems = activeTab === 'reservations' ? reservationItems : items;
  const displayCount = activeTab === 'reservations' ? reservationRows.length : totalCount;

  // V255.2: 테이블 행 — 예약 탭은 예약 단위, 그 외는 직원 단위
  const tableDisplayRows: { item: EmploymentManagementItem; key: string; reservationType?: DetailTabType }[] =
    activeTab === 'reservations'
      ? reservationRows.map((r) => ({
          item: r.item,
          key: `${r.item.historyId}-${r.reservationType}`,
          reservationType: r.reservationType,
        }))
      : displayItems.map((item) => ({
          item,
          key: item.historyId,
        }));

  // V46 Fix(A): items가 갱신되면 selectedItem을 최신 객체로 자동 동기화
  // V47: 예약정보 탭에서는 displayItems 기준으로 동기화
  useEffect(() => {
    if (!selectedItem) {
      return;
    }

    const latest = displayItems.find(i => i.historyId === selectedItem.historyId);

    // 최신 데이터에 해당 직원이 있으면 최신 객체로 교체
    if (latest && latest !== selectedItem) {
      setSelectedItem(latest);
      return;
    }

    // 최신 데이터에서 사라졌으면 선택 해제 (예약정보 탭에서 예약 없는 직원 포함)
    if (!latest) {
      setSelectedItem(null);
      return;
    }
  }, [displayItems, selectedItem]);

  // V253.2: URL 파라미터에서 상태 복원 (안전 패턴)
  // - URL 파라미터 없으면 아무 것도 안 함 (ref 설정 안 함)
  // - URL 있지만 목록에 아직 없으면 대기 (ref 설정 안 함)
  // - 찾으면 1회 복원 후 가드 설정
  const urlRestoredRef = useRef(false);
  useEffect(() => {
    if (urlRestoredRef.current) return;
    if (displayItems.length === 0) return;

    const urlAction = searchParams.get('action') as EmploymentActionType | null;
    const urlHistoryId = searchParams.get('historyId');

    // URL 복원 케이스가 아닐 경우 아무 것도 하지 않음
    if (!urlAction || !urlHistoryId) return;
    if (!ACTION_TO_DETAIL_TAB[urlAction]) return;

    const found = displayItems.find(i => i.historyId === urlHistoryId);

    if (!found) return; // 아직 목록에 없으면 대기

    // 여기서 최초 1회만 가드 설정
    urlRestoredRef.current = true;

    setSelectedItem(found);
    setSelectedAction(ACTION_TO_DETAIL_TAB[urlAction]);
    setShowDetailModal(true);
  }, [displayItems, searchParams]);

  const scheduleTermination = useScheduleTermination();
  const changeTerminationDate = useChangeTerminationDate();
  const cancelTermination = useCancelTermination();
  const extendContract = useExtendContract();
  const changeEmploymentType = useChangeEmploymentType();
  const manualTrigger = useManualTriggerEmploymentTypeChange(); // V55.28

  // 액션 핸들러
  const handleScheduleTermination = async (plannedEndDate: string, reason?: string) => {
    if (!selectedItem) return;
    // V45.2: terminationReasonCode 기본값 'OTHER' 사용
    await scheduleTermination.mutate(selectedItem.historyId, plannedEndDate, 'OTHER', reason);
    setShowTerminationModal(false);
    setSelectedItem(null);
    // V45.1: refetch()는 현재 activeTab을 유지하여 재조회 (탭 유지 보장)
    refetch();
  };

  const handleChangeTerminationDate = async (newPlannedEndDate: string) => {
    if (!selectedItem) return;
    await changeTerminationDate.mutate(selectedItem.historyId, newPlannedEndDate);
    setShowTerminationModal(false);
    setSelectedItem(null);
    // V45.1: refetch()는 현재 activeTab을 유지하여 재조회 (탭 유지 보장)
    refetch();
  };

  const handleCancelTermination = async (item: EmploymentManagementItem) => {
    if (!window.confirm(`${item.employeeName}님의 퇴사 예약을 취소하시겠습니까?`)) return;
    await cancelTermination.mutate(item.historyId);
    // V45.1: refetch()는 현재 activeTab을 유지하여 재조회 (탭 유지 보장)
    refetch();
  };

  const handleExtendContract = async (newEndDate: string) => {
    if (!selectedItem) return;
    await extendContract.mutate(selectedItem.historyId, newEndDate);
    setShowExtensionModal(false);
    setSelectedItem(null);
    // V45.1: refetch()는 현재 activeTab을 유지하여 재조회 (탭 유지 보장)
    refetch();
  };

  const handleChangeEmploymentType = async (
    newTypeCode: EmploymentTypeCode,
    effectiveDate: string,
    reason?: string,
    conversionBonusYears?: number // V61: 정규직 전환 시 전환가산
  ) => {
    if (!selectedItem) return;
    // V61: 정규직 전환 시 conversionBonusYears 전달 (endDate는 정규직은 불필요하므로 undefined)
    await changeEmploymentType.mutate(
      selectedItem.historyId,
      newTypeCode,
      effectiveDate,
      reason,
      undefined, // endDate (정규직은 필요 없음)
      conversionBonusYears
    );
    setShowTypeChangeModal(false);
    setSelectedItem(null);
    // V45.1: refetch()는 현재 activeTab을 유지하여 재조회 (탭 유지 보장)
    refetch();
  };

  // V55.28 → V55.30: 고용형태 변경 예약 수동 실행 핸들러 (진단 정보 포함)
  const handleManualTrigger = async () => {
    if (!window.confirm(
      '예약된 고용형태 변경을 즉시 실행하시겠습니까?\n\n' +
      '• 적용 예정일이 오늘 이전인 모든 예약이 처리됩니다.\n' +
      '• 이 작업은 되돌릴 수 없습니다.'
    )) {
      return;
    }

    try {
      const result = await manualTrigger.mutate();
      const { diagnostics } = result;

      // V55.30: 진단 정보 포함 메시지
      let message = `✅ ${result.message}\n\n`;
      message += `📊 진단 정보 (기준일: ${diagnostics.todayKST})\n`;
      message += `• 쿼리 매칭: ${diagnostics.matchedByQueryCount}건\n`;
      message += `• 날짜 적격: ${diagnostics.eligibleByDateCount}건\n`;
      message += `• 실제 처리: ${diagnostics.processedCount}건\n`;

      if (diagnostics.matchedByQueryCount > 0 && diagnostics.processedCount === 0) {
        message += `\n⚠️ 스킵 사유:\n`;
        if (diagnostics.skippedReasonCounts.futureDate > 0) {
          message += `  - 미래 예정일: ${diagnostics.skippedReasonCounts.futureDate}건\n`;
        }
        if (diagnostics.skippedReasonCounts.missingEffectiveDate > 0) {
          message += `  - 적용일 누락: ${diagnostics.skippedReasonCounts.missingEffectiveDate}건\n`;
        }
        if (diagnostics.skippedReasonCounts.missingTargetCode > 0) {
          message += `  - 대상코드 누락: ${diagnostics.skippedReasonCounts.missingTargetCode}건\n`;
        }
        if (diagnostics.skippedReasonCounts.updateFailed > 0) {
          message += `  - 업데이트 실패: ${diagnostics.skippedReasonCounts.updateFailed}건\n`;
        }
      }

      if (diagnostics.sampleMatchedIds.length > 0) {
        message += `\n🔍 샘플 대상:\n`;
        diagnostics.sampleMatchedIds.forEach(id => {
          message += `  - ${id}\n`;
        });
      }

      alert(message);
      // 처리 후 목록 새로고침
      refetch();
    } catch (err: any) {
      alert(`❌ 실행 실패: ${err.message}`);
    }
  };

  // V253: 액션 선택 핸들러
  const handleActionSelect = (action: EmploymentActionType) => {
    if (!selectedItem) return;
    const detailTab = ACTION_TO_DETAIL_TAB[action];
    setSelectedAction(detailTab);
    setShowActionPicker(false);
    setShowDetailModal(true);
    setEnteredViaActionPicker(true);
    setSearchParams({ tab: 'detail', historyId: selectedItem.historyId, action });
  };

  // 상태 뱃지 렌더링 (V45.4 → V46 Fix: 계약연장예정 > 고용형태변경 > 퇴사예정 > 만료임박)
  const renderStatusBadge = (item: EmploymentManagementItem) => {
    // A. 계약 연장 예정 (V46 Fix: status뿐만 아니라 contractExtensionPlanned 필드도 직접 확인)
    if (item.status === 'extensionPlanned' || item.contractExtensionPlanned) {
      return (
        <span
          style={{
            padding: '2px 8px',
            background: '#ecfdf5',
            color: '#059669',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          계약연장 예정
        </span>
      );
    }
    // B. 고용형태 변경 예정 (V46 Fix: status뿐만 아니라 employmentTypeChangePlanned 필드도 직접 확인)
    if (item.status === 'typeChangePlanned' || item.employmentTypeChangePlanned) {
      return (
        <span
          style={{
            padding: '2px 8px',
            background: '#ede9fe',
            color: '#7c3aed',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          고용형태 변경 예정
        </span>
      );
    }
    // C. 퇴사예정
    if (item.status === 'scheduled') {
      return (
        <span
          style={{
            padding: '2px 8px',
            background: '#fef2f2',
            color: '#dc2626',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          퇴사예정
        </span>
      );
    }
    // D. 만료 임박 / 만료
    if (item.status === 'expiring') {
      const isExpired = item.endDate && item.endDate < getTodayYMD();
      if (isExpired) {
        return (
          <span
            style={{
              padding: '2px 8px',
              background: '#fef2f2',
              color: '#dc2626',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            만료
          </span>
        );
      }
      return (
        <span
          style={{
            padding: '2px 8px',
            background: '#fffbeb',
            color: '#d97706',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          만료임박
        </span>
      );
    }
    // E. 해당 없음
    return (
      <span
        style={{
          padding: '2px 8px',
          background: 'var(--color-gray-100)',
          color: 'var(--color-gray-500)',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        -
      </span>
    );
  };

  // V255.2: 예약 탭 전용 — 예약 타입 기반 뱃지 (우선순위 추측 제거)
  const renderReservationBadge = (reservationType: DetailTabType) => {
    const config: Record<DetailTabType, { label: string; bg: string; color: string }> = {
      extension: { label: '계약연장 예정', bg: '#ecfdf5', color: '#059669' },
      typeChange: { label: '고용형태 변경 예정', bg: '#ede9fe', color: '#7c3aed' },
      termination: { label: '퇴사예정', bg: '#fef2f2', color: '#dc2626' },
      // V66 Phase 2: 휴직은 reservation 개념과 무관(별도 SSoT). 이 함수가 호출되지 않지만 타입 만족용 dummy.
      leaveOfAbsence: { label: '휴직', bg: '#fef3c7', color: '#92400e' },
    };
    const c = config[reservationType];
    return (
      <span style={{ padding: '2px 8px', background: c.bg, color: c.color, borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
        {c.label}
      </span>
    );
  };

  // D-day 계산
  const renderDday = (days?: number) => {
    if (days === undefined) return '-';
    if (days < 0) return <span style={{ color: '#dc2626' }}>D+{Math.abs(days)}</span>;
    if (days === 0) return <span style={{ color: '#dc2626' }}>D-Day</span>;
    if (days <= 7) return <span style={{ color: '#dc2626' }}>D-{days}</span>;
    if (days <= 30) return <span style={{ color: '#d97706' }}>D-{days}</span>;
    return <span style={{ color: 'var(--color-gray-500)' }}>D-{days}</span>;
  };

  // ========================================
  // V46: 퇴사일(예정) 컬럼 표시 로직
  // ========================================

  // String-Safe 날짜 헬퍼 (UTC toISOString 금지)
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const formatLocalYMD = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const parseLocalYMD = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const addDaysYMD = (dateStr: string, days: number) => {
    const dt = parseLocalYMD(dateStr);
    dt.setDate(dt.getDate() + days);
    return formatLocalYMD(dt);
  };
  const getNextDay = (dateStr: string) => addDaysYMD(dateStr, 1);
  // V254.2-1: KST 기준 today (UTC+9 고정)
  const getTodayYMD = (): string => {
    const now = new Date();
    const kstOffset = 9 * 60 * 60 * 1000;
    const kstDate = new Date(now.getTime() + kstOffset);
    return kstDate.toISOString().split('T')[0];
  };

  /**
   * V76 UI 규칙: 계약만료임박/직원검색 탭 - "퇴사처리일" 표시 값 계산
   *
   * V76 정책: endDate = leaveDate (계약만료일 = 퇴사일, +1일 정책 폐지)
   *
   * 우선순위 (leaveDate 사용 금지):
   * (1) terminationPlanned=true AND terminationPlannedEndDate 존재 → terminationPlannedEndDate (그대로)
   * (2) 퇴사 예약 없음 → endDate (계약만료일 = 퇴사일)
   * (3) 둘 다 없음 → undefined ("-" 표시)
   */
  const getDisplayLeaveDate = (item: EmploymentManagementItem): string | undefined => {
    // V76: (1) 퇴사 예약이 있으면 → terminationPlannedEndDate (= leaveDate)
    if (item.terminationPlanned && item.terminationPlannedEndDate) {
      return item.terminationPlannedEndDate;
    }
    // V76: (2) 퇴사 예약 없으면 → 계약만료일(endDate = leaveDate)
    if (item.endDate) {
      return item.endDate;
    }
    // (3) 둘 다 없으면 → undefined
    return undefined;
  };

  // V255.2: 예약 타입 기반 적용 예정일 (우선순위 추측 제거)
  const getReservationEffectiveDateByType = (item: EmploymentManagementItem, type: DetailTabType): string | undefined => {
    switch (type) {
      case 'termination': return item.terminationPlannedEndDate;
      case 'extension': return item.contractExtensionPlannedEffectiveDate;
      case 'typeChange': return item.employmentTypeChangePlannedEffectiveDate;
      // V66 Phase 2: 휴직은 reservation 개념 밖
      case 'leaveOfAbsence': return undefined;
    }
  };

  // V255.2: 예약 타입 기반 실행일 (우선순위 추측 제거)
  const getReservationRunDateByType = (item: EmploymentManagementItem, type: DetailTabType): string | undefined => {
    const effectiveDate = getReservationEffectiveDateByType(item, type);
    let runDate: string | undefined;
    switch (type) {
      case 'termination': runDate = item.terminationPlannedRunDate; break;
      case 'extension': runDate = item.contractExtensionPlannedRunDate; break;
      case 'typeChange': runDate = item.employmentTypeChangePlannedRunDate; break;
      // V66 Phase 2: 휴직은 reservation 개념 밖
      case 'leaveOfAbsence': runDate = undefined; break;
    }
    if (!runDate || runDate === effectiveDate) return undefined;
    return runDate;
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* V45.3: 페이지 타이틀 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 600,
          margin: '0 0 20px 0',
          color: 'var(--color-gray-900)',
        }}
      >
        고용 관리
      </h1>

      {/* 탭 */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '16px',
          background: 'white',
          padding: '4px',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              // V255: 탭 전환 시 모달/선택 상태 전체 초기화
              setSelectedItem(null);
              setSelectedAction(null);
              setShowActionPicker(false);
              setShowDetailModal(false);
              setEnteredViaActionPicker(false);
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: activeTab === tab.id ? 'var(--color-primary)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--color-gray-600)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* 필터 영역 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 히스토리 탭이 아닐 때만 고용형태 필터 표시 */}
          {activeTab !== 'history' && (
            <select
              value={currentFilters.employmentTypeCode}
              onChange={(e) => updateCurrentFilter('employmentTypeCode', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                minWidth: '120px',
              }}
            >
              {EMPLOYMENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.name}
                </option>
              ))}
            </select>
          )}

          {/* 검색 - V45.4: 탭별 필터 */}
          <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '300px' }}>
            <Search
              size={18}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-gray-400)',
              }}
            />
            <input
              type="text"
              value={currentFilters.searchQuery}
              onChange={(e) => updateCurrentFilter('searchQuery', e.target.value)}
              placeholder="이름 검색..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 40px',
                border: '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* V45.2: 히스토리 탭일 때는 날짜 범위 입력 */}
          {activeTab === 'history' ? (
            <>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>조회 기간:</label>
                <input
                  type="date"
                  value={historyStartDate}
                  onChange={(e) => setHistoryStartDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
                <span style={{ color: 'var(--color-gray-400)' }}>~</span>
                <input
                  type="date"
                  value={historyEndDate}
                  onChange={(e) => setHistoryEndDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    fontSize: '14px',
                  }}
                />
              </div>
              <button
                onClick={() => {
                  setHistoryPage(1);
                  historyHook.fetch(historyStartDate, historyEndDate, {
                    searchQuery: currentFilters.searchQuery,
                  });
                }}
                disabled={historyHook.isLoading || !historyStartDate || !historyEndDate}
                style={{
                  padding: '8px 16px',
                  background: historyStartDate && historyEndDate ? 'var(--color-primary)' : 'var(--color-gray-300)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: historyHook.isLoading || !historyStartDate || !historyEndDate ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <Search size={16} />
                조회
              </button>
              {/* V47: 다운로드 버튼 */}
              <button
                onClick={async () => {
                  if (!historyStartDate || !historyEndDate) return;
                  try {
                    const logs = await historyHook.fetchForExport(
                      historyStartDate,
                      historyEndDate,
                      currentFilters.searchQuery
                    );
                    const filename = `employment_history_${historyStartDate}_${historyEndDate}.csv`;
                    exportEmploymentHistoryToCSV(logs, filename);
                  } catch (err) {
                    console.error('다운로드 실패:', err);
                    alert('다운로드 중 오류가 발생했습니다.');
                  }
                }}
                disabled={historyHook.isLoading || !historyStartDate || !historyEndDate}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: 'var(--color-gray-700)',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  cursor: historyHook.isLoading || !historyStartDate || !historyEndDate ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                }}
              >
                <Download size={16} />
                다운로드
              </button>
            </>
          ) : (
            /* V46: 직원검색 탭은 검색 버튼 클릭 시 카운터 증가로 조회 트리거 */
            <button
              onClick={() => {
                if (activeTab === 'search') {
                  setSearchTriggerCount(prev => prev + 1);
                } else {
                  refetch();
                }
              }}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                background: activeTab === 'search' ? 'var(--color-primary)' : 'white',
                color: activeTab === 'search' ? 'white' : 'inherit',
                border: activeTab === 'search' ? 'none' : '1px solid var(--color-gray-300)',
                borderRadius: '8px',
                cursor: isLoading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: activeTab === 'search' ? 500 : 400,
              }}
            >
              {activeTab === 'search' ? (
                <>
                  <Search size={16} />
                  검색
                </>
              ) : (
                <>
                  <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                  새로고침
                </>
              )}
            </button>
          )}

          {/* V244: 예약 강제 적용 버튼 — 예약 정보 탭에서만 노출 */}
          {activeTab === 'reservations' && (
            <div
              style={{ position: 'relative' }}
              title={!isAdmin ? '관리자만 사용 가능' : ''}
            >
              <button
                onClick={handleManualTrigger}
                disabled={!isAdmin || manualTrigger.isLoading}
                style={{
                  padding: '8px 16px',
                  background: isAdmin ? '#7c3aed' : 'var(--color-gray-200)',
                  color: isAdmin ? 'white' : 'var(--color-gray-400)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: !isAdmin || manualTrigger.isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: manualTrigger.isLoading ? 0.7 : 1,
                }}
              >
                <PlayCircle size={16} className={manualTrigger.isLoading ? 'animate-spin' : ''} />
                {manualTrigger.isLoading ? '실행 중...' : '예약 강제 적용'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* V55.3 → V69 (2026-05-20): 계약만료 임박 탭 안내 문구 — 노출 기준 D-30 → 만료일 전월 1일 변경 (contract_expiring 알림 통일) */}
      {activeTab === 'expiring' && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--color-blue-50, #EFF6FF)',
            borderRadius: '8px',
            fontSize: '13px',
            color: 'var(--color-blue-700, #1D4ED8)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          계약만료일 기준 전월 1일부터 노출됩니다. (예: 만료일 06-10 → 05-01부터 노출)
        </div>
      )}

      {/* 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '14px', color: 'var(--color-gray-600)' }}>
            {/* V15 Issue 1: 직원검색 탭에서 searchTriggerCount가 0이면 카운트 숨김 */}
            {activeTab === 'search' && searchTriggerCount === 0 ? (
              <span>검색 조건을 입력한 뒤 검색 버튼을 눌러주세요</span>
            ) : (
              <>
                총{' '}
                <strong>
                  {/* V47: 예약정보 탭은 displayCount 사용 */}
                  {activeTab === 'history' ? historyHook.totalCount : displayCount}
                </strong>
                건
              </>
            )}
          </span>
        </div>

        {/* V45.2: 히스토리 탭일 때는 별도 렌더링 */}
        {activeTab === 'history' ? (
          <>
            {/* 히스토리 초기 로딩 (데이터 없을 때만 전체 로딩 표시) */}
            {historyHook.isLoading && historyHook.logs.length === 0 && (
              <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                히스토리를 불러오는 중...
              </div>
            )}

            {/* 히스토리 에러 */}
            {historyHook.error && (
              <div style={{ padding: '60px', textAlign: 'center', color: '#dc2626' }}>
                히스토리를 불러오는 중 오류가 발생했습니다.
              </div>
            )}

            {/* 히스토리 데이터 없음 */}
            {!historyHook.isLoading && !historyHook.error && historyHook.logs.length === 0 && (
              <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                {!historyStartDate || !historyEndDate ? (
                  <div>조회 기간을 선택한 뒤 조회 버튼을 눌러주세요.</div>
                ) : (
                  '해당 기간에 히스토리가 없습니다.'
                )}
              </div>
            )}

            {/* 히스토리 테이블 (데이터 있으면 로딩 중에도 유지) */}
            {!historyHook.error && historyHook.logs.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', opacity: historyHook.isLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                <thead>
                  <tr style={{ background: 'var(--color-gray-50)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      실행 일시
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      액션
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      직원
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      사번
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      이메일
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      적용일
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      실행자
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginateData(historyHook.logs, historyPage, HISTORY_PAGE_SIZE).map((log) => (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: '1px solid var(--color-gray-100)',
                      }}
                    >
                      {/* 정책 (V62-3): log.companyCode(대상 직원 소속 회사) 기준 시차.
                          HR이 다른 국가여도 대상 직원 소속 회사 시간으로 표시.
                          동일 고용관리 로그 내 시간 기준 통일 목적. */}
                      <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'center' }}>
                        {formatDateTimeByCompanyCode(log.executedAt, log.companyCode)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 500, textAlign: 'center' }}>
                        {log.actionTypeName || ACTION_TYPE_NAMES[log.actionType] || log.actionType}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'center' }}>
                        {log.employeeName}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-gray-600)', textAlign: 'center' }}>
                        {log.employeeNumber || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-gray-600)', textAlign: 'center' }}>
                        {log.email || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', textAlign: 'center' }}>
                        {log.effectiveDate}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: 'var(--color-gray-600)', textAlign: 'center' }}>
                        {log.executedByName || '관리자'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* V71: 페이지네이션 */}
            {!historyHook.error && historyHook.logs.length > 0 && (
              <Pagination
                currentPage={historyPage}
                totalItems={historyHook.logs.length}
                pageSize={HISTORY_PAGE_SIZE}
                onPageChange={setHistoryPage}
              />
            )}
          </>
        ) : (
          <>
            {/* 기존 탭 (검색/만료임박/예약정보) */}
            {/* V15 Issue 1: 직원검색 탭에서 searchTriggerCount가 0이면 초기 상태 표시 */}
            {activeTab === 'search' && searchTriggerCount === 0 ? (
              <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                검색 조건을 입력한 뒤 검색 버튼을 눌러주세요.
              </div>
            ) : (
              <>
                {/* V45.5: 로딩 또는 탭 데이터 불일치 시 로딩 UI 표시 (깜빡임 방지) */}
                {(isLoading || isTabDataMismatch) && (
                  <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                    데이터를 불러오는 중...
                  </div>
                )}

                {/* 에러 */}
                {!isTabDataMismatch && error && (
                  <div style={{ padding: '60px', textAlign: 'center', color: '#dc2626' }}>
                    데이터를 불러오는 중 오류가 발생했습니다.
                  </div>
                )}

                {/* 데이터 없음 - V45.4: 탭별 필터 사용, V45.5: 탭 불일치 시 숨김 */}
                {/* V47: displayItems 사용 (예약정보 탭은 예약 있는 항목만) */}
                {!isLoading && !isTabDataMismatch && !error && displayItems.length === 0 && (
                  <div style={{ padding: '60px', textAlign: 'center', color: 'var(--color-gray-500)' }}>
                    {activeTab === 'reservations' ? '예약된 항목이 없습니다.' : '검색 결과가 없습니다.'}
                  </div>
                )}
              </>
            )}

            {/* 테이블 - V45.5: 탭 불일치 시 숨김 (깜빡임 방지) */}
            {/* V47: displayItems 사용 (예약정보 탭은 예약 있는 항목만) */}
            {!isLoading && !isTabDataMismatch && !error && displayItems.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-gray-50)' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      이름
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      사번
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      이메일
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      고용형태
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      최초입사일
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      계약종료일
                    </th>
                    {/* V47: 예약정보 탭에서만 "적용 예정일" 컬럼 표시 */}
                    {activeTab === 'reservations' && (
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                        적용 예정일
                      </th>
                    )}
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 500 }}>
                      상태
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* V255.2: tableDisplayRows — 예약 탭은 예약 단위, 그 외는 직원 단위 */}
                  {tableDisplayRows.map(({ item, key: rowKey, reservationType }) => (
                    <tr
                      key={rowKey}
                      onClick={() => {
                        if (reservationType) {
                          // V255.2: 예약 행 → 해당 예약 상세 직접 진입
                          setSelectedItem(item);
                          setSelectedAction(reservationType);
                          setShowDetailModal(true);
                        } else if (!isAdmin) {
                          // V61: role_exec 등 조회 전용 → 액션 피커 없이 상세 보기 직접 진입 (readOnly)
                          setSelectedItem(item);
                          setSelectedAction(null);
                          setShowDetailModal(true);
                        } else {
                          // 일반 탭 → 액션 피커
                          setSelectedItem(item);
                          setShowActionPicker(true);
                        }
                      }}
                      style={{
                        borderBottom: '1px solid var(--color-gray-100)',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-gray-50)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', fontWeight: 500 }}>
                        {item.employeeName}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: 'var(--color-gray-600)' }}>
                        {item.employeeNumber || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: 'var(--color-gray-600)' }}>
                        {item.email || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px' }}>
                        {item.employmentTypeName}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px' }}>
                        {item.firstHireDate || item.startDate}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px' }}>
                        {item.endDate ? (
                          <div>
                            <div>{item.endDate}</div>
                            <div style={{ fontSize: '12px' }}>{renderDday(item.daysUntilEndDate)}</div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-gray-400)' }}>-</span>
                        )}
                      </td>
                      {/* V255.2: 예약정보 탭에서만 "적용 예정일" 컬럼 표시 (reservationType 기반) */}
                      {activeTab === 'reservations' && (
                        <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px' }}>
                          {(() => {
                            if (!reservationType) return <span style={{ color: 'var(--color-gray-400)' }}>-</span>;
                            const effectiveDate = getReservationEffectiveDateByType(item, reservationType);
                            const runDate = getReservationRunDateByType(item, reservationType);
                            if (effectiveDate) {
                              return (
                                <>
                                  <div style={{ color: '#2563eb', fontWeight: 500 }}>
                                    {effectiveDate}
                                  </div>
                                  {/* V254: 실행일이 효력일과 다른 경우만 표시 */}
                                  {runDate && (
                                    <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '2px' }}>
                                      실행: {runDate}
                                    </div>
                                  )}
                                </>
                              );
                            }
                            return <span style={{ color: 'var(--color-gray-400)' }}>-</span>;
                          })()}
                        </td>
                      )}
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {reservationType ? renderReservationBadge(reservationType) : renderStatusBadge(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* V253: 액션 선택 모달 */}
      {showActionPicker && selectedItem && (
        <ModalOverlay
          onClose={() => {
            setShowActionPicker(false);
            setSelectedItem(null);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '440px',
              padding: '28px',
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
              처리할 항목을 선택하세요
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: 'var(--color-gray-600)' }}>
              {selectedItem.employeeName} ({selectedItem.employmentTypeName})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {ACTION_PICKER_OPTIONS.map((opt) => (
                <button
                  key={opt.action}
                  onClick={() => handleActionSelect(opt.action)}
                  style={{
                    padding: '14px 16px',
                    background: 'white',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left' as const,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = opt.color;
                    e.currentTarget.style.background = `${opt.color}08`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '15px', color: opt.color, marginBottom: '2px' }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-gray-500)' }}>
                    {opt.description}
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setShowActionPicker(false);
                setSelectedItem(null);
              }}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '10px',
                background: 'var(--color-gray-100)',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                color: 'var(--color-gray-600)',
                cursor: 'pointer',
              }}
            >
              취소
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* V45.2 → V253: 고용 상세 관리 모달 (forceTab으로 단일 액션만 렌더) */}
      <EmploymentDetailManagementModal
        isOpen={showDetailModal}
        readOnly={!isAdmin}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedItem(null);
          setSelectedAction(null);
          setEnteredViaActionPicker(false);
          // V253.1: 우리가 추가한 key만 제거, 나머지 query는 유지
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('tab');
            next.delete('historyId');
            next.delete('action');
            return next;
          });
        }}
        item={selectedItem}
        forceTab={selectedAction || undefined}
        onBack={enteredViaActionPicker ? () => {
          // V255: 상세 모달 닫고 → 액션 피커로 복귀 (selectedItem 유지)
          setShowDetailModal(false);
          setSelectedAction(null);
          setEnteredViaActionPicker(false);
          setShowActionPicker(true);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('tab');
            next.delete('historyId');
            next.delete('action');
            return next;
          });
        } : undefined}
        onSuccess={async () => {
          // V46 Fix(A): 단순히 refetch만 수행 (selectedItem 동기화는 useEffect가 자동 처리)
          await refetch();
        }}
      />

      {/* 기존 개별 모달들 (하위 호환성) */}
      <TerminationScheduleModal
        isOpen={showTerminationModal}
        onClose={() => {
          setShowTerminationModal(false);
          setSelectedItem(null);
        }}
        onSubmit={terminationModalMode === 'schedule' ? handleScheduleTermination : handleChangeTerminationDate}
        item={selectedItem}
        mode={terminationModalMode}
        isLoading={scheduleTermination.isLoading || changeTerminationDate.isLoading}
      />

      <ContractExtensionModal
        isOpen={showExtensionModal}
        onClose={() => {
          setShowExtensionModal(false);
          setSelectedItem(null);
        }}
        onSubmit={handleExtendContract}
        item={selectedItem}
        isLoading={extendContract.isLoading}
      />

      <EmploymentTypeChangeModal
        isOpen={showTypeChangeModal}
        onClose={() => {
          setShowTypeChangeModal(false);
          setSelectedItem(null);
        }}
        onSubmit={handleChangeEmploymentType}
        item={selectedItem}
        isLoading={changeEmploymentType.isLoading}
      />
    </div>
  );
}

export default HrEmploymentManagementPage;
